import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { getCachedChart } from '../services/chartCacheService';

// Helper: concurrency-limited fetch runner with immediate updates
async function runWithConcurrencyAndUpdates(tasks, concurrency, onUpdate) {
  const results = new Array(tasks.length);
  let completed = 0;
  let i = 0;
  
  const run = async () => {
    while (i < tasks.length) {
      const idx = i++;
      try {
        const result = await tasks[idx]();
        results[idx] = result;
        completed++;
        
        // Immediately update UI when a task completes
        if (onUpdate) {
          const currentResults = results.filter(r => r !== undefined);
          onUpdate(currentResults, completed, tasks.length);
        }
      } catch (e) {
        results[idx] = undefined;
        completed++;
        console.error(`[Sparklines] Error in task ${idx}:`, e.message);
      }
    }
  };
  
  const runners = Array.from({ length: concurrency }, run);
  await Promise.all(runners);
  return results;
}

// Main hook
export function useCoinSparklines(coins, percentChanges, { points = 24, refreshMinutes = 10, concurrency = 5, priorityCount = 10 } = {}) {
  const [charts, setCharts] = useState({});
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const mountedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;

    const fetchCharts = async () => {
      if (!coins || coins.length === 0) {
        setCharts({});
        setProgress({ completed: 0, total: 0 });
        return;
      }

      setLoading(true);
      setProgress({ completed: 0, total: coins.length });

      // Create tasks for all coins
      const tasks = coins.map((symbol, index) => async () => {
        try {
          // Handle symbol mapping for chart providers
          let symbolToUse = symbol;
          if (symbol === 'Bonk') {
            symbolToUse = 'BONK';
          } else if (symbol === '$WIF') {
            symbolToUse = 'WIF';
          }
          
          const data = await getCachedChart(symbolToUse, '24h', points, refreshMinutes);
          return { symbol, data, priority: index < priorityCount };
        } catch (e) {
          console.error(`[Sparklines] Error fetching ${symbol}:`, e.message);
          return { symbol, data: null, priority: index < priorityCount };
        }
      });

      // Update function that gets called immediately when each chart loads
      const onUpdate = (currentResults, completed, total) => {
        if (cancelled || !mountedRef.current) return;
        
        // Build charts object from completed results
        const newCharts = {};
        for (const result of currentResults) {
          if (result && result.data) {
            newCharts[result.symbol] = result.data;
          }
        }
        
        // Update state immediately
        setCharts(newCharts);
        setProgress({ completed, total });
        
        // Log progress for debugging
      };

      try {
        await runWithConcurrencyAndUpdates(tasks, concurrency, onUpdate);
      } catch (error) {
        console.error('[Sparklines] Error in batch fetch:', error);
      } finally {
        if (!cancelled && mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchCharts();
    
    // Set up refresh interval
    const interval = setInterval(fetchCharts, refreshMinutes * 60 * 1000);
    
    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [coins, points, refreshMinutes, concurrency, priorityCount]);

  // Helper for color
  const getColor = symbol => {
    const percent = percentChanges[symbol];
    return percent >= 0 ? '#21b26f' : '#ff6b6b';
  };

  // Debug function to check cache status
  const debugCache = () => {
    console.log('[Sparklines] Cache status:', {
      currentCoins: coins,
      priorityCount,
      fetchOrder: coins.map((coin, index) => `${index + 1}. ${coin}${index < priorityCount ? ' (PRIORITY)' : ''}`).join(', '),
      progress,
      chartsLoaded: Object.keys(charts).length
    });
  };

  // Expose debug function in development
  if (process.env.NODE_ENV === 'development') {
    window.debugSparklineCache = debugCache;
    window.debugChartCache = () => {
      const { debugChartCache } = require('../services/chartCacheService');
      debugChartCache();
    };
  }

  return { charts, getColor, debugCache, loading, progress };
} 